import { cloneDeep, cloneDeepWith } from 'lodash';

/** Clone editable settings, keeping Angular-owned TemplateRef objects opaque. */
export function cloneGridDraft<T>(value: T): T {
  return cloneDeepWith(value, (entry, key) => key === 'titleTemplate' ? entry : undefined);
}

/** Decode historical arrays and current objects at the persistence boundary. */
export function decodeGridState(value: any): any {
  const parsed = typeof value === 'string' ? JSON.parse(value) : value;
  if (Array.isArray(parsed)) return { columns: cloneGridDraft(parsed) };
  if (parsed == null) return {};
  if (typeof parsed !== 'object') throw new Error('Invalid saved grid state');
  return cloneGridDraft(parsed);
}

export function gridStatePayload(view: any, columns?: any[]): any {
  const state = decodeGridState(view.gridColumns);
  const layout = columns ?? view.columns ?? state.columns;
  // TemplateRef objects belong to Angular, never to persisted preferences.
  const clean = layout?.map(({ titleTemplate, ...column }) => cloneDeep(column));
  const gridColumns = clean ? { columns: clean, ...state } : state;
  if (clean) gridColumns.columns = clean;
  return { id: view.id, gridId: view.gridId, name: view.name,
    isDefault: !!view.isDefault, gridColumns: JSON.stringify(gridColumns) };
}

export function defaultGridView(views: any[], id?: number): any {
  return views.find(view => view.id === id) || views.find(view => view.isDefault)
    || views.find(view => !view.name) || views[0];
}

/** Saved layouts must not contain rows, selection, paging or open editors. */
export function agGridPreferences(state: any): any {
  const keys = ['version', 'columnOrder', 'columnSizing', 'columnVisibility',
    'columnPinning', 'sort', 'rowGroup', 'pivot', 'aggregation'];
  return Object.fromEntries(keys.filter(key => key in state)
    .map(key => [key, cloneDeep(state[key])]));
}

/** An explicit empty filter/sort is a value; switching views never restores old rows. */
export function gridReturnState(historyState: any, viewId: number, switching: boolean): any {
  if (switching || !historyState?.gridData || historyState.gridData.parentKey) return null;
  if (historyState.gridState != null && historyState.gridState !== viewId) return null;
  const state = cloneDeep(historyState.gridData);
  delete state.columns; // Layout is restored from its saved view, independently of cached rows.
  return state;
}
